import React from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import styles from './feedItemStyles';

import FeedItemContainer from 'buyside/components/container/Feed/FeedItemContainer';
import FeedItemLoading from 'buyside/components/presentation/Feed/FeedItem/feedItemLoading';
import FeedItemEmpty from 'buyside/components/presentation/Feed/FeedItem/feedItemEmpty';
import Link from 'buyside/components/presentation/Link/link';
import JobVault from 'buyside/components/presentation/Feed/JobVault/jobVault';
import Ranking from 'buyside/components/presentation/Feed/Ranking/ranking';
import NewAllocator from 'buyside/components/presentation/Feed/NewAllocator/newAllocator';
import NewFund from 'buyside/components/presentation/Feed/NewFund/newFund';
import FundUpdate from 'buyside/components/presentation/Feed/FundUpdate/fundUpdate';
import Post from 'buyside/components/presentation/Feed/Post/post';
import AdminPostContainer from 'buyside/components/container/Feed/AdminPostContainer';
import CorporateAction from 'buyside/components/presentation/Feed/CorporateAction/corporateAction';
import Idea from 'buyside/components/presentation/Feed/Idea/idea';
import IdeaUpdate from 'buyside/components/presentation/Feed/IdeaUpdate/ideaUpdate';
import PublishedComment from 'buyside/components/presentation/Feed/PublishedComment/publishedComment';
import Feature from 'buyside/components/presentation/Feed/Feature/feature';
import LightbulbIcon from 'buyside/svgs/lightbulb_2.svg';
import WhitePaper from 'buyside/components/presentation/Feed/WhitePaper/WhitePaperFeedItem';
import { newIdeaUrl, inviteMembersUrl } from 'buyside/util/urlHelper';

//   Config Options
//   actions if the feed item has an actions bar, includes likes
//   eventObjectName the name of the prop that the event object is, leave blank to not pass it down
//   component the react component that needs to be rendered for the body of the feed item
//   clickHandlerName used in the create generateClickHandler function, leave blank

const feedItemConfig = {
  CORPORATE_ACTION: {
    actions: false,
    eventObjectName: 'corporateAction',
    component: CorporateAction
  },
  FEATURE: {
    actions: false,
    eventObjectName: 'featuredItem',
    component: Feature,
    clickHandlerName: 'featured_idea_click'
  },
  FILL_JOB_POSTING: {
    actions: false,
    eventObjectName: 'jobPosting',
    component: JobVault
  },
  PUBLISH_ADMIN_POST: {
    actions: false,
    eventObjectName: 'adminPost',
    component: AdminPostContainer
  },
  PUBLISH_ALLOCATOR_PROFILE: {
    actions: false,
    eventObjectName: 'allocator',
    component: NewAllocator
  },
  PUBLISH_COMMENT: {
    actions: false,
    eventObjectName: 'comment',
    component: PublishedComment,
    clickHandlerName: 'idea_comment_click'
  },
  PUBLISH_FUND_PROFILE: {
    actions: false,
    eventObjectName: 'fundProfile',
    component: NewFund
  },
  PUBLISH_FUND_PROFILE_UPDATE: {
    actions: false,
    eventObjectName: 'fundUpdate',
    component: FundUpdate
  },
  PUBLISH_IDEA: {
    actions: false,
    eventObjectName: 'idea',
    component: Idea,
    clickHandlerName: 'idea_click'
  },
  PUBLISH_IDEA_UPDATE: {
    actions: true,
    eventObjectName: 'ideaUpdate',
    component: IdeaUpdate,
    clickHandlerName: 'idea_update_click'
  },
  PUBLISH_JOB_POSTING: {
    actions: false,
    eventObjectName: 'jobPosting',
    component: JobVault
  },
  PUBLISH_POST: {
    actions: true,
    eventObjectName: 'post',
    component: Post
  },
  PUBLISH_RANK: {
    actions: false,
    component: Ranking
  },
  PUBLISH_WHITE_PAPER: {
    actions: false,
    eventObjectName: 'whitePaper',
    component: WhitePaper
  }
};

const FeedItems = ({
  feedItems,
  currentUser,
  feedType,
  isLoadingEntries,
  recommendations,
  isPublic
}) => {
  if (feedType === 'Entity' && (!feedItems || !feedItems.edges || !feedItems.edges.length)) {
    return (
      <FeedItemEmpty>
        <div className={styles.emptyEntityFeed}>
          <LightbulbIcon className={styles.bigBulb} />
          <h4 className={styles.emptyHeader}>Got any bright ideas?</h4>
          <p className={styles.emptyEntityText}>
            There’s currently no research for this company.
            {currentUser && currentUser.canWriteIdeas && (
              <span>
                {' '}
                Be the first to <Link to={newIdeaUrl()}>submit your own research</Link> on this or
                any other company.
              </span>
            )}
            <span>
              {' '}
              If you know someone who might be an expert on this company, why not
              <Link to={inviteMembersUrl()}> invite them to SumZero</Link>?
            </span>
          </p>
        </div>
      </FeedItemEmpty>
    );
  }

  return (
    <>
      <TransitionGroup exit={feedType !== 'Entity'}>
        {feedItems.edges.map((edge, index) => {
          const config = feedItemConfig[edge.node.event.verb];
          if (feedType === 'Entity') {
            config.comments = false;
          }

          if (
            edge.node.event.eventObject.commentObject &&
            edge.node.event.eventObject.commentObject.__typename === 'Post'
          ) {
            config.actions = true;
          }

          return (
            <CSSTransition classNames="fade" timeout={1000} key={`${edge.node.id}-${index}`}>
              <FeedItemContainer
                {...edge.node}
                config={config}
                index={index}
                currentUser={currentUser}
                feedType={feedType}
                recommendations={recommendations}
                isPublic={isPublic}
              />
            </CSSTransition>
          );
        })}
      </TransitionGroup>
      {feedItems.pageInfo.hasNextPage && isLoadingEntries && <FeedItemLoading times={3} />}
    </>
  );
};

export default FeedItems;
